import React from 'react';
import classNames from 'classnames';

import { observer } from 'mobx-react';
import { Route, Router, Switch, useLocation } from 'react-router-dom';

import { HistoryModalStore, Modal } from 'ui/base/modal';
import { useStyles } from 'ui/base/theme';

import { sleep } from 'services/base/time';
import { Optional } from 'services/base/types';
import { configurePaymentRedirect, PAYMENT_REDIRECT } from 'services/base/config';
import { PaymentRedirectType } from 'services/base/redirect_types';

import {
  IAccountsDomain,
  Pangolin,
  Payment,
  PlanFirewall,
  PlanFirewallBilling,
  PlanType,
} from 'services/accounts/types';

import { PaymentsService } from 'services/accounts/payments_service';

import { getPaymentMethodName } from 'services/accounts/logic/payments/payments';

import {
  getPlanFirewallBillingName,
  getPlanFirewallPackageName,
  getPlanFirewallPackageTier,
  getPlanFirewallPlanActivePackage,
  getPlanFirewallSubscriptionActivePackage,
} from 'services/accounts/logic/plans/plan_firewall';

import {
  themedPlanFirewallPackageIconStyles,
  themedPlanFirewallBillingIconStyles,
  getPlanFirewallPackageIcon,
  getPlanFirewallBillingIcon,
} from 'assets/enums/plans/firewall/plan_firewall';

import { PlanItemFirewallBillingModal } from './modals/plan_firewall_billing_modal';
import { PlanItemFirewallConfirmModal } from './modals/plan_firewall_confirm_modal';
import { PlanItemFirewallHeaderModal } from './modals/plan_firewall_header_modal';
import { PlanItemFirewallPackageModal } from './modals/plan_firewall_package_modal';
import { PlanItemFirewallPaymentModal } from './modals/plan_firewall_payment_modal';
import { PlanItemFirewallStatusModal } from './modals/plan_firewall_status_modal';

import { PlanItemBase, PlanItemStyles, themedPlanItemStyles } from '../plan_item_base';

import { PlanItemFirewallStore } from './plan_item_firewall_store';

import Messages from './plan_item_firewall_messages';

export type PlanItemFirewallBaseRequiredModalProps = {
  styles: PlanItemStyles;
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  plan: PlanFirewall;
  store: PlanItemFirewallStore;
  modal: HistoryModalStore;
  root: string;
};

export type PlanItemFirewallBaseOptionalModalProps = {
  styles: PlanItemStyles;
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  plan: Optional<PlanFirewall>;
  store: PlanItemFirewallStore;
  modal: HistoryModalStore;
  root: string;
};

type PlanItemFirewallModalProps = PlanItemFirewallBaseOptionalModalProps;

const PlanItemFirewallModal = observer((props: PlanItemFirewallModalProps) => {
  const { plan, modal, root } = props;
  return (
    <Modal store={modal} onBackdropClick={modal.history.goBack}>
      <Router history={modal.history}>
        <Switch>
          <Route path={`${root}/header`}>
            <PlanItemFirewallHeaderModal {...props} />
          </Route>
          <Route path={`${root}/package`}>
            <PlanItemFirewallPackageModal {...props} />
          </Route>
          <Route path={`${root}/billing`}>
            <PlanItemFirewallBillingModal {...props} />
          </Route>
          <Route path={`${root}/payment`}>
            <PlanItemFirewallPaymentModal {...props} />
          </Route>
          <Route path={`${root}/confirm`}>
            <PlanItemFirewallConfirmModal {...props} />
          </Route>
          {plan && (
            <Route path={`${root}/status`}>
              <PlanItemFirewallStatusModal
                {...props}
                plan={plan}
              />
            </Route>
          )}
        </Switch>
      </Router>
    </Modal>
  );
});

type PlanItemFirewallPartialProps = {
  styles: PlanItemStyles;
  accounts: IAccountsDomain;
  plan: Optional<PlanFirewall>;
  modal: HistoryModalStore;
  root: string;
};

const PlanItemFirewallPartial = observer(({ styles, accounts, plan, modal, root }: PlanItemFirewallPartialProps) => {
  const packageIcons = useStyles(themedPlanFirewallPackageIconStyles);
  const billingIcons = useStyles(themedPlanFirewallBillingIconStyles);

  const payment: Optional<Payment> = plan?.payment_id ? accounts.payments.get(plan.payment_id) : undefined;

  const activePackage = getPlanFirewallPlanActivePackage(plan);
  const activeBillingIsLifetime = (getPlanFirewallPackageTier(activePackage) >
                                   getPlanFirewallPackageTier(getPlanFirewallSubscriptionActivePackage(plan)));

  const active = !!activePackage;
  const expired = !active && (plan?.license || plan?.subscription);

  const alertPackage = (
    plan?.package !== activePackage
  );
  const alertBilling = (
    (plan?.billing === PlanFirewallBilling.LIFETIME && !activeBillingIsLifetime) ||
    (plan?.billing !== PlanFirewallBilling.LIFETIME && activeBillingIsLifetime)
  );
  const alertStatus = (
    (!active) ||
    (plan?.subscription?.is_active && plan.subscription.is_terminal) ||
    (plan?.subscription?.is_active && !plan.subscription.is_terminal && !payment)
  );

  return (
    <div className={styles.planContent}>
      {plan?.package && (
        <button className={classNames(styles.field, styles.clickable)}
                type='button' onClick={() => modal.showModalAt(`${root}/package`)}>
          <div className={styles.fieldTextLeft}>
            {getPlanFirewallPackageName(plan.package)}
            {alertPackage && '*'}
            <div className={styles.fieldInfo}>
              {Messages.fieldPackage()}
            </div>
          </div>
          <div className={styles.fieldAlert}>
            {alertPackage && <div className={styles.alertCritical} />}
            <img className={styles.fieldIcon} alt='Package Icon'
                 src={getPlanFirewallPackageIcon(packageIcons, plan.package)} />
          </div>
          <div className={styles.open}/>
        </button>
      )}
      {plan?.billing && (
        <button className={classNames(styles.field, styles.clickable)}
                type='button' onClick={() => modal.showModalAt(`${root}/billing`)}>
          <div className={styles.fieldTextLeft}>
            {getPlanFirewallBillingName(plan.billing)}
            {alertBilling && '*'}
            <div className={styles.fieldInfo}>
              {Messages.fieldBilling()}
            </div>
          </div>
          <div className={styles.fieldAlert}>
            {alertBilling  && <div className={styles.alertCritical} />}
            <img className={styles.fieldIcon} alt='Billing Icon'
                 src={getPlanFirewallBillingIcon(billingIcons, plan.billing)} />
          </div>
          <div className={styles.open}/>
        </button>
      )}
      {payment && (
        <button className={classNames(styles.field, styles.clickable)}
                type='button' onClick={() => modal.showModalAt(`${root}/payment`)}>
          <div className={styles.fieldTextLeft}>
            {getPaymentMethodName(payment)}
            <div className={styles.fieldInfo}>
              {Messages.fieldPayment()}
            </div>
          </div>
          <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
          <div className={styles.open}/>
        </button>
      )}
      {(
        <button className={classNames(styles.field, styles.clickable)}
                type='button' onClick={() => modal.showModalAt(active || expired ? `${root}/status` : `${root}/header`)}>
          <div className={styles.fieldTextLeft}>
            {active ? Messages.statusActive() :
             expired ? Messages.statusExpired()
                     : Messages.statusInactive()}
            {alertStatus && '*'}
            <div className={styles.fieldInfo}>
              {Messages.fieldStatus()}
            </div>
          </div>
          <div className={styles.fieldAlert}>
            {alertStatus && <div className={styles.alertCritical} />}
            {active ? (
              <img className={styles.fieldIcon} src={styles.fieldIconImageStatusGood} alt='Good Icon' />
            ) : (
              <img className={styles.fieldIcon} src={styles.fieldIconImageStatusWarn} alt='Warning Icon' />
            )}
          </div>
          <div className={styles.open}/>
        </button>
      )}
    </div>
  );
});

type PlanItemFirewallProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  plan: Optional<PlanFirewall>;
  id: Optional<string>;
};

export const PlanItemFirewall = observer(({ accounts, pangolin, plan, id }: PlanItemFirewallProps) => {
  const location = useLocation();
  const styles = useStyles(themedPlanItemStyles);

  const root = `${location.pathname}/firewall`;

  // Create plan store

  const [store, setStore] = React.useState<PlanItemFirewallStore>(() => (
    new PlanItemFirewallStore(plan, accounts.payments)
  ));

  // Create modal store

  const [modal] = React.useState(() => new HistoryModalStore(false));
  React.useEffect(() => { return modal.handleGoBack(); }, [modal]);

  // Handle payment redirect (platform: web)

  const [redirect] = React.useState(() => {
    const redirect = PAYMENT_REDIRECT;
    if (!redirect) return;
    if (redirect.type !== PaymentRedirectType.PLAN) return;
    if (redirect.pangolin !== pangolin.id) return;
    if (redirect.plan !== PlanType.FIREWALL) return;
    configurePaymentRedirect(undefined);
    return redirect;
  });

  const [redirecting, setRedirecting] = React.useState(false);
  const [redirected, setRedirected] = React.useState(false);

  React.useEffect(() => {
    if (!redirect) return;
    if (redirecting) return;
    if (redirected) return;
    (async(redirect) => {
      let first = true;
      const end = Date.now() + 5000;
      const lastPaymentsCount = redirect.lastPaymentsCount;
      while (Date.now() <= end && accounts.payments.size === lastPaymentsCount) {
        // eslint-disable-next-line no-await-in-loop
        await PaymentsService.fetchAll(accounts);
        // eslint-disable-next-line no-await-in-loop
        if (!first) await sleep(1000);
        first = false;
      }
      redirect.history.forEach((path, index) => {
        if (index === 0) modal.history.replace(path);
        else modal.history.push(path);
      });
      setRedirecting(true);
      modal.showModal();
    })(redirect);
  });

  // Handle modal display and final payment redirect (if applicable)

  React.useEffect(() => {
    if (!modal.visible) return;
    const store = new PlanItemFirewallStore(plan, accounts.payments);
    if (!redirect) { setStore(store); return; }
    if (!redirecting) { setStore(store); return; }
    if (redirected) { setStore(store); return; }
    store.package_store.setSelected(redirect.package);
    store.billing_store.setSelected(redirect.billing);
    setRedirected(true); setStore(store);
  }, [modal.visible]);

  // Handle header click

  const onHeaderClick = React.useCallback(() => modal.showModalAt(`${root}/header`), [modal, root]);

  return (
    <>
      <PlanItemFirewallModal
        styles={styles}
        accounts={accounts}
        pangolin={pangolin}
        plan={plan}
        store={store}
        modal={modal}
        root={root}
      />
      <PlanItemBase
        id={id}
        type={PlanType.FIREWALL}
        onHeaderClick={onHeaderClick}>
        <PlanItemFirewallPartial
          styles={styles}
          accounts={accounts}
          plan={plan}
          modal={modal}
          root={root}
        />
      </PlanItemBase>
    </>
  );
});
